import { Table, TableWrapper, Tbody, Td, Tr } from '@components/core-ui/Table';
import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import TrashBin from '@components/icons/TrashBin.svg';
import { dateToInX } from '@helpers/dateToInX';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PrismaOrganizationApiKeyDto } from 'app/API';
import { apiKeysKeys } from 'app/queries/apiKeys/apiKeysKeys';
import { createFetchOrganizationApiKeysQueryFn } from 'app/queries/apiKeys/createFetchOrganizationApiKeysQueryFn';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { LoadError } from '../LoadError';
import { CellContent } from './styles/CellContent';
import { LastCellContent } from './styles/LastCellContent';
import { Wrapper } from './styles/Wrapper';

const EMPTY_KEYS: PrismaOrganizationApiKeyDto[] = [];

export const ApiKeysList = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [disclosureOpen, setDisclosureOpen] = useState<boolean>(true);
  const { openModal, closeModal } = useModalContext();
  const { organization } = useAuthContext();
  const orgId = organization?.id ?? '';
  const keysListKey = apiKeysKeys.list(orgId);

  const keysQuery = useQuery({
    queryKey: keysListKey,
    queryFn: createFetchOrganizationApiKeysQueryFn(call),
    enabled: orgId !== '',
  });
  const keys = keysQuery.data ?? EMPTY_KEYS;

  const sectionTitle = `API Keys${keys.length > 0 ? ` (${keys.length})` : ''}`;

  const refreshKeys = () => {
    closeModal();
    void queryClient.invalidateQueries({ queryKey: keysListKey });
  };

  const createAPIKey = () => {
    openModal(ModalNamesEnum.CreateAPIKey, {
      onSuccess: refreshKeys,
    });
  };

  const deleteAPIKey = (data: PrismaOrganizationApiKeyDto) => {
    openModal(ModalNamesEnum.DeleteAPIKey, {
      id: data.id,
      organizationId: data.organizationId,
      onSuccess: refreshKeys,
    });
  };

  return (
    <Wrapper className="flex flex-col">
      <div>
        <DisclosureSection
          title={sectionTitle}
          open={disclosureOpen}
          setOpenState={setDisclosureOpen}
          loading={keysQuery.isLoading}
        >
          {keysQuery.isError ? (
            <LoadError message="Failed to load API keys." onRetry={() => void keysQuery.refetch()} />
          ) : null}
          <TableWrapper>
            <Table>
              <Tbody>
                {keys.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <CellContent style={{ width: '350px' }}>{data.id}</CellContent>
                    </Td>
                    <Td>
                      <CellContent>
                        <span style={{ display: 'block' }}>
                          {data.expirationDate
                            ? new Date(data.expirationDate) > new Date()
                              ? `Expires: ${dateToInX(data.expirationDate, false)}`
                              : 'Expired'
                            : 'Never expires'}
                        </span>
                      </CellContent>
                    </Td>
                    <Td style={{ width: '1%' }}>
                      <LastCellContent>
                        {organization?.permissions?.manageOrganization ? (
                          <TrashBin
                            className="hover:text-[var(--color-title)] text-[var(--color-text-muted)] cursor-pointer"
                            onClick={() => deleteAPIKey(data)}
                          />
                        ) : null}
                      </LastCellContent>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
          {keys.length < 5 ? (
            <div className="grid place-items-end">
              <div className="w-36">
                <OutlinedButton className="my-2 w-[140px]" disabled={false} onClick={createAPIKey}>
                  Create new API Key
                </OutlinedButton>
              </div>
            </div>
          ) : null}
        </DisclosureSection>
      </div>
    </Wrapper>
  );
};
